import { Component, Input, OnInit, ViewChild, inject } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SharedService } from '@shared/services/shared.service';
import { IDocument } from 'app/models/documents/document';
import { IDocumentType } from 'app/models/documents/documentType';
import { IStatus } from 'app/models/licenses/status';
import { DocumentService } from 'app/services/document/document.service';

@Component({
  selector: 'document',
  templateUrl: './document.component.html',
  styleUrls: ['./document.component.scss']
})
export class DocumentComponent implements OnInit {
  @Input() entityId: number = 0;
  @Input() entityType: string = '';
  @Input() statuses: IStatus[] = [];

  public documents: IDocument[] = [];
  public documentTypes: IDocumentType[] = [];
  public selectedTypes = new FormControl<IDocumentType[] | null>([]);
  public includeArchived: boolean = false;

  public documentColumns: string [] = ['name', 'types', 'archived', 'actions'];
  public documentSource = new MatTableDataSource<IDocument>(this.documents);
  @ViewChild('documentPaginator', {static: false}) documentPaginator: MatPaginator;
  @ViewChild('documentSort', {static: false}) documentSort: MatSort;

  public sharedService = inject(SharedService);
  public documentService = inject(DocumentService);

  ngOnInit(): void {
    this.getDocumentTypes();
    this.getDocuments(this.includeArchived);
  }

  getDocumentTypes(): void {
    this.documentService.getDocumentTypes().subscribe({
      next: response => this.documentTypes = response,
      error: e => console.log('Error downloading document types', e)
    });
  }

  getDocuments(includeArchived: boolean): void {
    this.includeArchived = includeArchived;
    this.documentService.getDocuments(this.entityId, this.entityType, includeArchived).subscribe({
      next: response => this.documents = response,
      error: e => {
        this.sharedService.toastr.error("Error getting documents");
        console.log('Error getting documents', e);
      },
      complete: () => this.updateDocumentTable()
    });
  }

  updateDocumentTable(): void {
    setTimeout(() => {
      this.documentSource.data = this.documents;
      this.documentSource.sort = this.documentSort;
      this.documentSource.paginator = this.documentPaginator;
    }, 500);
  }

  uploadDocument(event: Event): void {
    let dirtyFile = (event.target as HTMLInputElement).files[0];
    let file = new File([dirtyFile], dirtyFile.name.replace(/[^A-Za-z0-9.]/g, ''));
    let types: IDocumentType[] = this.selectedTypes.value;

    let upload: IDocument = {
      id: 0,
      name: file.name,
      extension: '',
      parentId: this.entityId,
      adminOnly: false,
      archived: false,
      types: types
    }

    const formData = new FormData();
    formData.append("file", file, file.name);
    formData.append("document", JSON.stringify(upload));
    this.documentService.uploadDocument(formData, this.entityType).subscribe({
      next: response => this.documents.push(response),
      error: e => {
        (event.target as HTMLInputElement).value = '';
        this.sharedService.toastr.error("Could not upload document");
        console.log('Error uploading document', e);
      },
      complete: () => {
        (event.target as HTMLInputElement).value = '';
        this.selectedTypes.setValue([]);
        this.updateDocumentTable();
      }
    });
  }

  downloadDocument(documentId: number, documentName: string): void {
    this.documentService.downloadDocument(documentId).subscribe({
      next: response => this.saveFile(documentName, response),
      error: e => {
        this.sharedService.toastr.error("Could not download document");
        console.log('Error downloading document', e);
      }
    });
  }

  saveFile(fileName: string, blob: Blob) {
    let file = URL.createObjectURL(blob);
    var fileDownload = document.createElement("a");
    fileDownload.href = file;
    fileDownload.download = fileName;
    fileDownload.click();
  }

  archiveDocument(document: IDocument, archive: boolean): void {
    let archiving: string = archive? 'Archive ' : 'Un-Archive ';
    this.sharedService.openConfirm(archiving + 'this document?');
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.documentService.archiveDocument(document.id, archive).subscribe({
            complete: () => {
              document.archived = archive;
              if(archive){
                this.documents = this.documents.filter(d => d.id !== document.id);
              }
              this.updateDocumentTable();
            },
            error: e => {
              this.sharedService.toastr.error('Could not archive document');
              console.log('Error archiving document', e);
            }
          });
        }
      }
    );
  }

  deleteDocument(documentId: number): void {
    this.sharedService.openConfirm("Delete this document?");
    this.sharedService.confirmed().subscribe(
      confirmed => {
        if(confirmed){
          this.documentService.deleteDocument(documentId).subscribe({
            complete: () => {
              this.documents = this.documents.filter(d => d.id !== documentId);
              this.updateDocumentTable();
            },
            error: e => {
              this.sharedService.toastr.error("Could not delete document");
              console.log('Error deleting document', e);
            }
          });
        }
      }
    );
  }

  public get locked(): boolean {
    return this.statuses.some(s => s.label === this.sharedService.submitted);
  }

}
